import "./AddressModal.css";
import React, { useEffect, useState } from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import {
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { fetchCountries } from "../../../redux/actions/countries.actions";
import { getUserAddressApi, IUserAddress, updateUserAddressApi } from "../../../services/api/user.api";
import ModalShell, { ModalLoading } from "./ModalShell";

interface AddressModalProps {
  open: boolean,
  setOpen: (open: boolean) => void,
}

const emptyAddress: IUserAddress = {
  address1: null,
  address2: null,
  address3: null,
  locality: null,
  region: null,
  post_code: null,
  country_id: null,
};

// The address is private to its owner, so it lives in local state only and never in Redux.
const AddressModal: React.FC<AddressModalProps> = ({open, setOpen}) => {
  const dispatch: AppDispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.accessToken);
  const {data: countries} = useSelector((state: RootState) => state.countries);

  const [address, setAddress] = useState<IUserAddress>(emptyAddress);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<"editing" | "success" | "error">("editing");
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open && (!countries || countries.length === 0)) {
      dispatch(fetchCountries());
    }
  }, [open, countries, dispatch]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    getUserAddressApi(token)
      .then((data) => {
        setAddress(data ?? emptyAddress);
      })
      .catch(() => {
        setStatus("error");
        setErrorMessage("Unable to load your address. Please try again later.");
      })
      .finally(() => setLoading(false));
  }, [open, token]);

  const handleClose = () => {
    setOpen(false);
    setStatus("editing");
    setErrorMessage("");
    setFieldErrors({});
    setAddress(emptyAddress);
  };

  const setField = (field: keyof IUserAddress, value: string) => {
    setAddress({...address, [field]: value === '' ? null : value});
    setFieldErrors({...fieldErrors, [field]: ''});
  };

  // Blanks every part; the country stays because it's required
  const handleClearAll = () => {
    setAddress({...emptyAddress, country_id: address.country_id});
    setFieldErrors({});
  };

  const handleSave = async () => {
    setSaving(true);
    setFieldErrors({});
    try {
      const saved = await updateUserAddressApi(token, address);
      setAddress(saved);
      setStatus("success");
    } catch (error: any) {
      const errors = error?.response?.data?.errors;
      if (errors && typeof errors === 'object') {
        const messages: Record<string, string> = {};
        Object.entries(errors).forEach(([field, val]: [string, any]) => {
          messages[field] = Array.isArray(val) ? val.join(' ') : String(val);
        });
        setFieldErrors(messages);
      } else {
        setStatus("error");
        setErrorMessage(error?.response?.data?.data?.message ?? error?.response?.data?.message ?? "Unable to update address. Please try again later.");
      }
    } finally {
      setSaving(false);
    }
  };

  const textField = (field: keyof IUserAddress, label: string, autoComplete: string) => (
    <TextField
      label={label}
      variant="outlined"
      fullWidth
      size="small"
      value={address[field] ?? ''}
      onChange={(e) => setField(field, e.target.value)}
      error={!!fieldErrors[field]}
      helperText={fieldErrors[field]}
      autoComplete={autoComplete}
    />
  );

  const done = status !== "editing";

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size={loading || done ? "sm" : "md"}
      busy={saving}
      tone={loading ? undefined : status === "error" ? "error" : status === "success" ? "success" : undefined}
      title={loading ? "Edit address" : status === "error" ? "An error occurred" : status === "success" ? "Address updated" : "Edit address"}
      actions={loading ? undefined : done ?
        <button className="btn btn-primary" onClick={handleClose}>OK</button>
        :
        <>
          <button className="btn btn-ghost dlg-actions-start address-modal-clear" onClick={handleClearAll} disabled={saving}>Clear all</button>
          <button className="btn" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !address.country_id}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {loading ?
        <ModalLoading/>
      :
      status === "error" ?
        <p>{errorMessage}</p>
      :
      status === "success" ?
        <p>Your address was successfully updated.</p>
      :
        <>
          {textField('address1', 'Address line 1', 'address-line1')}
          {textField('address2', 'Address line 2', 'address-line2')}
          {textField('address3', 'Address line 3', 'address-line3')}
          <div className="dlg-row">
            {textField('locality', 'City', 'address-level2')}
            {textField('region', 'Region / Province / State', 'address-level1')}
          </div>
          <div className="dlg-row">
            {textField('post_code', 'Postal code', 'postal-code')}
            <FormControl fullWidth size="small" required error={!!fieldErrors.country_id}>
              <InputLabel id="address-country-label">Country</InputLabel>
              <Select
                labelId="address-country-label"
                label="Country"
                value={address.country_id ?? ''}
                onChange={(e) => {
                  setAddress({...address, country_id: e.target.value === '' ? null : Number(e.target.value)});
                  setFieldErrors({...fieldErrors, country_id: ''});
                }}
              >
                {countries?.length > 0 && countries.map((country: any) =>
                  <MenuItem key={country?.id} value={country?.id}>{country?.label}</MenuItem>
                )}
              </Select>
              {fieldErrors.country_id && <FormHelperText>{fieldErrors.country_id}</FormHelperText>}
            </FormControl>
          </div>
        </>
      }
    </ModalShell>
  );
};

export default AddressModal;
